<template>
  <AiEducationLayout>
    <div class="ai-edu-admin-page">
      <ContentWrap title="家长关系">
        <el-alert
          class="mb-12px"
          type="info"
          :closable="false"
          title="仅「家长视图」已开启的学校可绑定。学生与家长请从下拉选择，勿手填编号。"
        />
        <el-form :inline="true" :model="query" class="-mb-15px">
          <el-form-item label="学校">
            <el-select
              v-model="query.schoolId"
              class="!w-240px"
              clearable
              filterable
              placeholder="选择学校"
              @change="onSchoolChange"
            >
              <el-option
                v-for="s in schools"
                :key="s.id"
                :label="schoolLabel(s)"
                :value="s.id!"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button @click="load"><Icon icon="ep:search" />搜索</el-button>
            <el-button
              v-hasPermi="['ai-education:parent-relation:write']"
              plain
              type="primary"
              @click="openDialog()"
            >
              <Icon icon="ep:plus" />绑定关系
            </el-button>
          </el-form-item>
        </el-form>
      </ContentWrap>

      <ContentWrap>
        <el-table v-loading="loading" :data="list" border>
          <el-table-column prop="schoolName" label="学校" min-width="140" />
          <el-table-column label="学生" min-width="140">
            <template #default="{ row }">
              <div>{{ row.studentName || '—' }}</div>
              <div class="text-12px text-[var(--el-text-color-secondary)]">{{ row.studentMobile }}</div>
            </template>
          </el-table-column>
          <el-table-column label="家长" min-width="140">
            <template #default="{ row }">
              <div>{{ row.parentName || '—' }}</div>
              <div class="text-12px text-[var(--el-text-color-secondary)]">{{ row.parentMobile }}</div>
            </template>
          </el-table-column>
          <el-table-column label="亲属关系" width="110">
            <template #default="{ row }">
              <dict-tag :type="DICT_TYPE.AI_EDU_PARENT_RELATIONSHIP" :value="row.relationship" />
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template #default="{ row }">
              <dict-tag :type="DICT_TYPE.AI_EDU_COMMON_STATUS" :value="row.status" />
            </template>
          </el-table-column>
          <el-table-column prop="startTime" label="生效时间" width="170" />
          <el-table-column prop="createTime" label="创建时间" width="170" />
          <el-table-column label="操作" width="180" fixed="right">
            <template #default="{ row }">
              <el-button
                v-hasPermi="['ai-education:parent-relation:write']"
                link
                type="primary"
                @click="openDialog(row)"
                >编辑</el-button
              >
              <el-button
                v-if="row.status === 20"
                v-hasPermi="['ai-education:parent-relation:write']"
                link
                type="warning"
                @click="handleDisable(row.id)"
                >停用</el-button
              >
              <el-button
                v-hasPermi="['ai-education:parent-relation:delete']"
                link
                type="danger"
                @click="handleDelete(row.id)"
                >删除</el-button
              >
            </template>
          </el-table-column>
        </el-table>
      </ContentWrap>

      <Dialog v-model="dialogVisible" :title="form.id ? '编辑家长关系' : '绑定家长关系'" width="520px">
        <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
          <el-form-item label="学校" prop="schoolId">
            <el-select
              v-model="form.schoolId"
              class="w-100%"
              filterable
              :disabled="!!form.id"
              @change="onFormSchoolChange"
            >
              <el-option
                v-for="s in parentCapableSchools"
                :key="s.id"
                :label="schoolLabel(s)"
                :value="s.id!"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="学生" prop="studentId">
            <el-select
              v-model="form.studentId"
              class="w-100%"
              filterable
              remote
              :remote-method="(kw: string) => loadMembers('student', kw)"
              :loading="memberLoading"
              placeholder="搜索学生昵称/手机"
            >
              <el-option
                v-for="m in studentOptions"
                :key="m.id"
                :label="memberLabel(m)"
                :value="m.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="家长" prop="parentId">
            <el-select
              v-model="form.parentId"
              class="w-100%"
              filterable
              remote
              :remote-method="(kw: string) => loadMembers('parent', kw)"
              :loading="memberLoading"
              placeholder="搜索家长昵称/手机"
            >
              <el-option
                v-for="m in parentOptions"
                :key="m.id"
                :label="memberLabel(m)"
                :value="m.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="亲属关系" prop="relationship">
            <el-select v-model="form.relationship" class="w-100%" placeholder="请选择">
              <el-option
                v-for="d in getStrDictOptions(DICT_TYPE.AI_EDU_PARENT_RELATIONSHIP)"
                :key="d.value"
                :label="d.label"
                :value="d.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="状态" prop="status">
            <el-select v-model="form.status" class="w-100%">
              <el-option
                v-for="d in getIntDictOptions(DICT_TYPE.AI_EDU_COMMON_STATUS)"
                :key="d.value"
                :label="d.label"
                :value="d.value"
              />
            </el-select>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button :loading="saving" type="primary" @click="submit">保存</el-button>
        </template>
      </Dialog>
    </div>
  </AiEducationLayout>
</template>

<script lang="ts" setup>
import * as AiEducationApi from '@/api/aiEducation'
import AiEducationLayout from '../layout/AiEducationLayout.vue'
import { DICT_TYPE, getDictLabel, getIntDictOptions, getStrDictOptions } from '@/utils/dict'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'AiEduParentRelation' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const memberLoading = ref(false)
const list = ref<AiEducationApi.ParentRelationVO[]>([])
const schools = ref<AiEducationApi.SchoolVO[]>([])
const studentOptions = ref<AiEducationApi.ParentMemberOptionVO[]>([])
const parentOptions = ref<AiEducationApi.ParentMemberOptionVO[]>([])
const query = reactive<{ schoolId?: number }>({})
const dialogVisible = ref(false)
const formRef = ref<FormInstance>()
const form = reactive<Partial<AiEducationApi.ParentRelationVO>>({})

const rules: FormRules = {
  schoolId: [{ required: true, message: '请选择学校', trigger: 'change' }],
  studentId: [{ required: true, message: '请选择学生', trigger: 'change' }],
  parentId: [{ required: true, message: '请选择家长', trigger: 'change' }],
  relationship: [{ required: true, message: '请选择亲属关系', trigger: 'change' }]
}

const parentCapableSchools = computed(() =>
  schools.value.filter((s) => s.parentEnabled === true || ['primary', 'junior', 'senior'].includes(s.eduStage || ''))
)

const schoolLabel = (s: AiEducationApi.SchoolVO) => {
  const stageLabel = s.eduStage
    ? getDictLabel(DICT_TYPE.AI_EDU_STAGE, s.eduStage) || s.eduStage
    : ''
  const stage = stageLabel ? ` · ${stageLabel}` : ''
  const parent = s.parentEnabled ? ' · 家长视图已开' : ''
  return `${s.name}${stage}${parent}`
}

const memberLabel = (m: AiEducationApi.ParentMemberOptionVO) =>
  `${m.nickname || '会员'}${m.mobile ? `（${m.mobile}）` : ''}`

const loadSchools = async () => {
  schools.value = (await AiEducationApi.getSchoolList()) || []
}

const load = async () => {
  loading.value = true
  try {
    list.value = (await AiEducationApi.getParentRelationList({ schoolId: query.schoolId })) || []
  } finally {
    loading.value = false
  }
}

const onSchoolChange = () => load()

const loadMembers = async (workspace: 'parent' | 'student', keyword = '') => {
  const schoolId = form.schoolId
  if (!schoolId) {
    message.warning('请先选择学校')
    return
  }
  memberLoading.value = true
  try {
    const rows =
      (await AiEducationApi.getParentMemberOptions({
        schoolId,
        workspace,
        keyword: keyword || undefined
      })) || []
    if (workspace === 'student') studentOptions.value = rows
    else parentOptions.value = rows
  } finally {
    memberLoading.value = false
  }
}

const onFormSchoolChange = async () => {
  form.studentId = undefined
  form.parentId = undefined
  studentOptions.value = []
  parentOptions.value = []
  await Promise.all([loadMembers('student'), loadMembers('parent')])
}

const openDialog = async (row?: AiEducationApi.ParentRelationVO) => {
  Object.assign(form, {
    id: row?.id,
    schoolId: row?.schoolId ?? query.schoolId ?? parentCapableSchools.value[0]?.id,
    studentId: row?.studentId,
    parentId: row?.parentId,
    relationship: row?.relationship || 'mother',
    status: row?.status ?? 20
  })
  dialogVisible.value = true
  if (form.schoolId) {
    await Promise.all([loadMembers('student'), loadMembers('parent')])
    if (row?.studentId && !studentOptions.value.some((m) => m.id === row.studentId)) {
      studentOptions.value.unshift({
        id: row.studentId,
        nickname: row.studentName,
        mobile: row.studentMobile,
        workspace: 'student'
      })
    }
    if (row?.parentId && !parentOptions.value.some((m) => m.id === row.parentId)) {
      parentOptions.value.unshift({
        id: row.parentId,
        nickname: row.parentName,
        mobile: row.parentMobile,
        workspace: 'parent'
      })
    }
  }
}

const submit = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    await AiEducationApi.saveParentRelation({
      id: form.id,
      schoolId: form.schoolId!,
      studentId: form.studentId!,
      parentId: form.parentId!,
      relationship: form.relationship!,
      status: form.status
    })
    message.success('保存成功')
    dialogVisible.value = false
    await load()
  } finally {
    saving.value = false
  }
}

const handleDisable = async (id?: number) => {
  if (!id) return
  await message.confirm('确认停用该家长关系？')
  await AiEducationApi.disableParentRelation(id)
  message.success('已停用')
  await load()
}

const handleDelete = async (id?: number) => {
  if (!id) return
  await message.delConfirm()
  await AiEducationApi.deleteParentRelation(id)
  message.success('已删除')
  await load()
}

onMounted(async () => {
  await loadSchools()
  const preferred =
    schools.value.find((s) => s.eduStage === 'primary') ||
    schools.value.find((s) => s.parentEnabled) ||
    schools.value[0]
  if (preferred?.id) query.schoolId = preferred.id
  await load()
})
</script>
